import React, { useCallback, useState, useEffect, useMemo } from 'react';

import {
  TbCloudDownload,
  TbDownload,
  TbArrowLeft,
  TbPlus,
  TbFileTypeCsv,
  TbFileTypeXls,
  TbFileTypePdf,
} from 'react-icons/tb';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { createLocale } from 'locales';

import useRedirect from 'hooks/use-redirect';
import useDownloadFile from 'hooks/use-download-file';

import { useOrganizationWeekPlanner } from 'state/context/organization/OrganizationWeekPlanner';
import useGetOrganizationAdminDetails from 'state/remote/remote-organization/use-get-organization-admin-details';
import useExportOrganisationJobsToFile from 'state/remote/remote-organization/use-export-organisation-jobs-to-file';

import Divider from 'components-new/divider/divider';
import Button from 'components-new/button/button';
import TextButton from 'components-new/text-button/text-button';
import Typography from 'components-new/typography/typography';
import Drawer from 'components-new/drawer/drawer';
import RadioNavigation from 'components-new/radio-navigation/radio-navigation';
import DropdownMenu from 'components-new/dropdown-menu/dropdown-menu';
import type { DropdownMenuItemType } from 'components-new/dropdown-menu/dropdown-menu-item';
import Icon from 'components-new/icon/icon';

import Tooltip from 'components-new/tooltip/tooltip';

import SidebarFilter from '../sidebar-filter';

import ContractsExportDialog from './contracts-export-dialog';
import GridNavigation from './grid-navigation';

const localize = createLocale('organizationWeekPlannerLocales');
const globalLocalize = createLocale('global');

const Header: React.FC = () => {
  const redirect = useRedirect();
  const navigate = useNavigate();
  const downloadFile = useDownloadFile();
  const [searchParams] = useSearchParams();

  const [fileType, setFileType] = useState<'csv' | 'xlsx'>('csv');
  const [exportButtonClicked, setExportButtonClicked] =
    useState<boolean>(false);
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);

  const { gridParams, tableParams, organizationPlannerSearchParams } =
    useOrganizationWeekPlanner();

  const {
    viewType,
    jobStatus,
    fromDate,
    jobType,
    viewRadioButtonElements,
    selectedFreelancers,
    selectedLocations,
    selectedOrganizations,
    toDate,
  } = organizationPlannerSearchParams;

  const { refetch: exportOrganisationJobsToFile } =
    useExportOrganisationJobsToFile({
      assignedStatus: jobStatus,
      fileType,
      fromDate: fromDate as string,
      isPagination: viewType === 'table',
      jobType,
      page: searchParams.get('pageNumber'),
      pageSize: searchParams.get('pageSize'),
      selectedFreelancers,
      selectedLocations,
      selectedOrganizations,
      toDate: toDate as string,
    });

  const [openContractsDialog, setOpenContractsDialog] =
    useState<boolean>(false);

  const exportFile = useCallback(async () => {
    const { data } = await exportOrganisationJobsToFile();
    if (data) {
      downloadFile(data, `opdrachten.${fileType}`);
    }
  }, [downloadFile, exportOrganisationJobsToFile, fileType]);

  useEffect(() => {
    if (exportButtonClicked) {
      exportFile();
      setExportButtonClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportButtonClicked]);

  const onExportFileButtonClicked = useCallback((file: 'csv' | 'xlsx') => {
    setFileType(file);
    setExportButtonClicked(true);
  }, []);

  const exportMenuItems: DropdownMenuItemType[] = [
    {
      key: '1',
      label: 'CSV',
      leadingElement: <Icon icon={TbFileTypeCsv} />,
      onClick: () => onExportFileButtonClicked('csv'),
    },
    {
      key: '2',
      label: 'XLSX',
      leadingElement: <Icon icon={TbFileTypeXls} />,
      onClick: () => onExportFileButtonClicked('xlsx'),
    },
    {
      key: '3',
      label: localize('contractsExport'),
      leadingElement: <Icon icon={TbFileTypePdf} />,
      onClick: () => setOpenContractsDialog(true),
    },
  ];

  const handleRedirect = () => {
    redirect('/organisation/jobs/place');
  };

  const navigateToIntegrationsPage = () => {
    return navigate('/organisation/settings/integrations');
  };

  const { numberOfSelectedJobs, setIsCancelBulkJobsDialogOpen } = tableParams;

  const {
    data: organisationAdminDetails,
    isLoading: isOrganisationAdminDetailsLoading,
    error: organisationAdminDetailsError,
  } = useGetOrganizationAdminDetails();

  // temporarily checking if "any" organisation exists with check_personenregister === true
  // when multi-tenancy is enabled for this page, this logic should be adjusted.
  const checkPersonenregister = useMemo(
    () => organisationAdminDetails?.some((org) => org.check_personenregister),
    [organisationAdminDetails],
  );

  const closeContractsDialog = useCallback(() => {
    setOpenContractsDialog(false);
  }, [setOpenContractsDialog]);

  const handleRedirectToPeriodJobs = () => {
    navigate('/organisation/period_jobs');
  };

  const handleRedirectToPersonRegister = () => {
    window.location.assign('/organisation/personenregister/');
  };

  return (
    <>
      <div className="flex flex-wrap justify-end items-center ml-auto gap-x-5 gap-y-2">
        <Typography
          variant="heading-1"
          as="h1"
          className="pr-8 mb-3 lg:mb-0 flex items-start self-start flex-1"
        >
          {localize('assignments')}
        </Typography>
        <TextButton
          label={globalLocalize('periodJobs')}
          variant="secondary"
          onClick={handleRedirectToPeriodJobs}
        />
        <RadioNavigation elements={viewRadioButtonElements} />
      </div>

      <div className="flex items-center justify-between xl:justify-end mt-2">
        <Button
          onClick={() => setIsDrawerOpened(true)}
          variant="transparent"
          className="xl:hidden mt-2 xl:mt-0"
          label={localize('filters')}
          leftIconProps={{ icon: TbArrowLeft }}
        />
        <Tooltip text={localize('personRegisterTooltip')}>
          <TextButton
            label={localize('personRegister')}
            variant="secondary"
            onClick={handleRedirectToPersonRegister}
          />
        </Tooltip>
      </div>
      {!isOrganisationAdminDetailsLoading &&
        !organisationAdminDetailsError &&
        checkPersonenregister && (
          <Typography
            as="p"
            variant="body-large"
            className="bg-destructive-100 text-brown p-4 rounded-md mt-6"
          >
            <strong>{globalLocalize('NB')}</strong>{' '}
            <a href="/organisation/personenregister/" className="underline">
              {localize('personenregisterAlertMessage')}
            </a>
          </Typography>
        )}

      <Divider className="mb-6 mt-2 xl:my-6" orientation="horizontal" />

      <div className="flex flex-col-reverse sm:flex-row gap-y-2 mb-2">
        {viewType === 'grid' && <GridNavigation gridParams={gridParams} />}
        <div className="flex flex-col-reverse w-full sm:flex-row items-center sm:justify-end gap-2 mb-2 sm:mb-0">
          <DropdownMenu
            menuButton={() => (
              <Button
                className="w-full sm:w-auto"
                label={globalLocalize('export')}
                leftIconProps={{
                  className: 'text-gray-500',
                  icon: TbDownload,
                }}
                variant="transparent"
              />
            )}
            items={exportMenuItems}
            menuItemsClassName="z-50 overflow-auto"
            position="bottom-end"
          />

          <Button
            className="w-full sm:w-auto"
            label={localize('importFromSDB')}
            variant="secondary"
            size="medium"
            leftIconProps={{
              icon: TbCloudDownload,
              size: 18,
            }}
            onClick={navigateToIntegrationsPage}
          />

          <Button
            className="w-full sm:w-auto"
            label={localize('addAssignment')}
            variant="primary"
            size="medium"
            leftIconProps={{
              icon: TbPlus,
            }}
            onClick={handleRedirect}
          />

          {numberOfSelectedJobs !== 0 && (
            <Button
              size="medium"
              label={`${localize('jobBulkCancel', {
                count: numberOfSelectedJobs,
              })}  (${localize('selectedJobs', {
                count: numberOfSelectedJobs,
              })})`}
              variant="danger"
              onClick={() => setIsCancelBulkJobsDialogOpen(true)}
              className="hidden"
            />
          )}
        </div>
      </div>

      <Drawer
        isOpen={isDrawerOpened}
        onClose={() => setIsDrawerOpened(false)}
        containerClassName="xl:hidden"
      >
        <SidebarFilter onDrawer />
      </Drawer>
      <ContractsExportDialog
        isOpen={openContractsDialog}
        onCloseClicked={closeContractsDialog}
      />
    </>
  );
};

export default Header;
